import React, { useState, useMemo } from 'react';
import { diff_match_patch } from 'diff-match-patch';
import { Box, Paper, Typography, TextField, Stack, useTheme } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function TextDiffChecker() {
  const theme = useTheme();
  const [text1, setText1] = useState('The quick brown fox jumps over the lazy dog.');
  const [text2, setText2] = useState('The quick brown fox jumps over the lazy cat.');

  const diffHtml = useMemo(() => {
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(text1, text2);
    dmp.diff_cleanupSemantic(diff);
    const html = dmp.diff_prettyHtml(diff);
    // The library uses inline styles, which is not ideal with CSP, but we'll accept for now.
    // A more robust solution would parse this and use classes.
    return html;
  }, [text1, text2]);

  const stats = useMemo(() => {
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(text1, text2);
    const additions = diff.filter(part => part[0] === 1).length;
    const deletions = diff.filter(part => part[0] === -1).length;
    return { additions, deletions };
  }, [text1, text2]);

  return (
    <>
      <SEO
        title="Text Diff Checker | Compare Text Differences | UmbrellaCo"
        description="Compare two text blocks to find differences instantly. Highlight additions, deletions, and modifications with our free online diff tool."
        keywords="text diff checker, compare text, diff tool online, text comparison, find text differences"
        url="/text-tools/diff-checker"
        jsonLd={createSoftwareAppSchema(
          'Text Diff Checker',
          'Compare two text strings to find additions and deletions instantly.',
          'https://umbrellaco.in/text-tools/diff-checker',
          ['Text Comparison', 'Diff Tool', 'Developer Utilities']
        )}
      />
      <ToolPageContainer
        title="Text Diff Checker"
        description="Compare two pieces of text side-by-side to highlight additions, deletions, and modifications."
        disablePadding
      >

      <Box sx={{ flexGrow: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, minHeight: 0 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Original Text</Typography>
          <TextField
            multiline
            value={text1}
            onChange={(e) => setText1(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' }, '& textarea': { height: '100% !important' } }}
          />
        </Paper>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Changed Text</Typography>
          <TextField
            multiline
            value={text2}
            onChange={(e) => setText2(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' }, '& textarea': { height: '100% !important' } }}
          />
        </Paper>
      </Box>

      <Paper sx={{ mt: 2, p: 2, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <CompareArrowsIcon color="primary" />
          <Typography>
            <Typography component="span" sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>{stats.additions} Additions</Typography>
            {' / '}
            <Typography component="span" sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>{stats.deletions} Deletions</Typography>
          </Typography>
        </Stack>
        <Paper
          variant="outlined"
          sx={{ p: 2, overflowX: 'auto', fontSize: '1rem', lineHeight: 1.7, maxHeight: '20vh' }}
          dangerouslySetInnerHTML={{ __html: diffHtml }}
        />
      </Paper>
      </ToolPageContainer>
    </>
  );
}

export default TextDiffChecker;
